<script setup>
import StartupsHeader from "@/components/ecosystem/StartupsHeader.vue";
import useGrid from "@/composables/useGrid";
import {getLogo} from "@/helpers/logo";
import {onMounted, ref, watch} from "vue";
import TablePager from "@/components/TablePager.vue";
import startupsService from "@/services/ecosystem/startups.service";
import {inviteStatuses} from "@/constants/constants";
import useStoreTrigger from "@/composables/useStoreTrigger";
import {useStore} from "vue2-helpers/vuex";
import AddToPortfolios from "@/components/startups/AddToPortfolios.vue";
import Invite from "@/components/startups/InviteStartup.vue";

const columns = [
    {
        label: 'Name',
        name: 'name',
        component: 'SearchCompanyNameColumn',
    },
    {
        label: 'Stage',
        name: 'stage',
    },
    {
        label: 'Location',
        name: 'location',
        component: 'SearchCompanyLocationColumn',
    },
    {
        label: 'Total Raised',
        name: 'total_raised',
    },
    {
        label: 'Status',
        name: 'status',
        component: 'InviteStatusColumn',
    },
    {
        name: 'actions',
        label: 'Actions',
        component: 'Actions',
    }
];

const {items, listLoading, gridParams, gridMethods} = useGrid(getIndexList);
const store = useStore();
const storeTrigger = useStoreTrigger();

const removing = ref(false);
const inviteFilter = ref('all');
const selectedStartups = ref([]);
const addStartupsToPortfolios = ref(null);
const invite = ref(null);

watch(inviteFilter, value => {
    gridParams.filters.invite_status = value
    gridMethods.resetList();
})

watch(() => store.state.needUpdateStartups, () => {
    if (store.state.needUpdateStartups) {
        gridMethods.resetList();
    }
})

onMounted(() => {
    gridMethods.setColumns(columns);
    gridMethods.resetList();
})

function getIndexList(pageParams) {
    return startupsService.getMyStartupsIndex(pageParams);
}

function applySearch(searchString) {
    gridParams.filters.query = searchString.trim();
    gridMethods.resetList();
}

async function remove(startupId) {
    if (confirm("Remove startup?")) {
        removing.value = true;
        await startupsService.deleteStartup(startupId);
        removing.value = false;

        gridMethods.resetList();
    }
}

function addToPortfolio(startupId) {
    selectedStartups.value = [startupId];
    addStartupsToPortfolios.value.open(startupId);
}
</script>

<template>
    <div class="mystartups-page">
        <header class="topbar mystartups-page__header">
            <StartupsHeader/>

            <input
                type="text"
                class="form-search-primary"
                placeholder="Search Startups"
                v-debounce:500ms.lock="applySearch"
                :disabled="listLoading"
            >
            <div class="topbar__side actions-list">
                <b-dropdown
                    id="dropdown-show"
                    :text="inviteStatuses[inviteFilter]"
                    class="topbar__side__actions topbar__side__actions--filter"
                >
                    <b-dropdown-item
                        v-for="(label, name) in inviteStatuses"
                        :key="name"
                        @click="inviteFilter = name"
                    >
                        {{label}}
                    </b-dropdown-item>
                </b-dropdown>

                <a class="link-wrapper ml-4">
                    <span class="link-wrapper__add" @click="storeTrigger('setAddStartup');">Add Startups</span>
                </a>
            </div>
        </header>

        <main class="main main--dark">
            <div class="main__inner">
                <div class="showing-select">
                    Showing {{gridParams.totalRowsCount}} startups
                </div>

                <ul class="list-cards">
                    <li v-for="startup in items" :key="startup.id">
                        <div class="image-block">
                            <img :src="getLogo(startup.name.logoUrl)" :alt="startup.name.name">
                        </div>
                        <div class="list-cards__content">
                            <router-link class="heading5 heading5--tertiary" :to="`/startups/${startup.name.id}`">
                                {{ startup.name.name }}
                            </router-link>

                            <p>{{ startup.name.shortDescription }}</p>

                            <ul class="list-stat">
                                <li class="list-stat__funding" v-if="startup.total_raised">{{ startup.total_raised }}</li>
                                <li class="list-stat__location" v-if="startup.location.country">{{ startup.location.country }}<span v-if="startup.location.region">, {{ startup.location.region }}</span></li>
                                <li class="list-stat__location" v-if="!startup.location.country && startup.location.region">{{ startup.location.region }}</li>
                                <li class="list-stat__startups" v-if="startup.stage">{{ startup.stage }}</li>
                            </ul>
                        </div>

                        <a
                            v-if="startup.status === 'invite'"
                            class="invite-info"
                            @click="invite.open(startup.id)"
                        >
                            <span class="invite-info__invited">{{ startup.status }}</span>
                        </a>

                        <p class="invite-info" v-if="startup.status !== 'invite'">
                            {{ startup.status }}
                        </p>

                        <b-dropdown right text="Actions" class="list-cards__actions icons-list dropdown-simple dropdown-small" :disabled="removing">
                            <b-dropdown-item @click="remove(startup.id)" class="icons-list__delete">
                                Remove startup
                            </b-dropdown-item>

                            <b-dropdown-item
                                @click="addToPortfolio(startup.id)"
                                class="icons-list__add"
                            >
                                Add to a portfolio
                            </b-dropdown-item>
                        </b-dropdown>
                    </li>
                </ul>

                <TablePager
                    v-if="items.length"
                    :pages-count="gridParams.pagesCount"
                    :init-page="gridParams.currentPage"
                    @changePage="gridMethods.changePage"
                />
            </div>
        </main>

        <AddToPortfolios
            ref="addStartupsToPortfolios"
            :startups="selectedStartups"
        />

        <Invite
            ref="invite"
            @invited="gridMethods.resetList"
        />
    </div>
</template>

<style scoped>

</style>
